/**
 * reportCatalogs.js — справочники бланка заключения. [11.1.31]
 * БЕЗ ЕДИНОГО ИМПОРТА (приём wlChips/organMath): чистые данные и чистые
 * функции, проверяемые verify без DOM.
 *
 * Просьбы владельца: выпадающий список контрастных препаратов, список
 * аппаратов лучевой диагностики с поиском, и чтобы поля дат сами
 * расставляли точки и проверяли введённое.
 */

// ══════════════════════════════════════════════════════════════
//  КОНТРАСТНЫЕ ПРЕПАРАТЫ
//  Разделены по типу: гадолиний (МР) и йод (КТ/ангио/рентген). Названия —
//  торговые, как их пишут в протоколе; в скобках МНН, чтобы врач узнавал
//  дженерики. Список — не рецепт: дозы и показания сюда не кладём.
// ══════════════════════════════════════════════════════════════

export const КОНТРАСТЫ_ГАДОЛИНИЙ = Object.freeze([
  'Гадовист (гадобутрол)',
  'Магневист (гадопентетовая кислота)',
  'Омнискан (гадодиамид)',
  'Дотарем (гадотеровая кислота)',
  'Кларискан (гадотеровая кислота)',
  'Гадовист-Виал (гадобутрол)',
  'Примовист (гадоксетовая кислота)',
  'Мультихенс (гадобеновая кислота)',
  'Оптимарк (гадоверсетамид)',
  'Прохэнс (гадотеридол)',
  'Гадопентетат-Виал (гадопентетовая кислота)',
  'Гадобутрол-Бинергия (гадобутрол)',
]);

export const КОНТРАСТЫ_ЙОД = Object.freeze([
  'Омнипак 300 (йогексол)',
  'Омнипак 350 (йогексол)',
  'Ультравист 300 (йопромид)',
  'Ультравист 370 (йопромид)',
  'Йопамиро 370 (йопамидол)',
  'Оптирей 350 (йоверсол)',
  'Визипак 320 (йодиксанол)',
  'Ксенетикс 350 (йобитридол)',
  'Йомерон 400 (йомепрол)',
  'Тразограф (амидотризоат)',
  'Урографин (амидотризоат)',
  'Липиодол (йодированное масло)',
  'Йогексол-Бинергия (йогексол)',
  'Йопромид-Виал (йопромид)',
]);

/** Список для поля «Контраст» по модальности: МР — гадолиний, остальное — йод. */
export function контрастыДляМодальности(modality) {
  const м = String(modality || '').toUpperCase();
  if (м === 'MR') return [...КОНТРАСТЫ_ГАДОЛИНИЙ, ...КОНТРАСТЫ_ЙОД];
  if (!м) return [...КОНТРАСТЫ_ЙОД, ...КОНТРАСТЫ_ГАДОЛИНИЙ];
  return [...КОНТРАСТЫ_ЙОД, ...КОНТРАСТЫ_ГАДОЛИНИЙ];
}

// ══════════════════════════════════════════════════════════════
//  АППАРАТЫ ЛУЧЕВОЙ ДИАГНОСТИКИ
//  Строки готовы к печати в бланке: «Компьютерный томограф Siemens
//  SOMATOM go.Top». Список охватывает то, что реально стоит в российских
//  клиниках, включая отечественные аппараты. Он ЗАВЕДОМО неполон — рынок
//  живой, — поэтому поле остаётся текстовым: список подсказывает, а не
//  запрещает.
// ══════════════════════════════════════════════════════════════

export const АППАРАТЫ = Object.freeze({
  CT: [
    'Компьютерный томограф Siemens SOMATOM go.Now',
    'Компьютерный томограф Siemens SOMATOM go.Top',
    'Компьютерный томограф Siemens SOMATOM Definition AS',
    'Компьютерный томограф Siemens SOMATOM Force',
    'Компьютерный томограф Siemens SOMATOM Drive',
    'Компьютерный томограф Siemens SOMATOM X.cite',
    'Компьютерный томограф GE Revolution EVO',
    'Компьютерный томограф GE Revolution CT',
    'Компьютерный томограф GE Optima CT660',
    'Компьютерный томограф GE BrightSpeed',
    'Компьютерный томограф Philips Incisive CT',
    'Компьютерный томограф Philips Ingenuity CT',
    'Компьютерный томограф Philips Brilliance 64',
    'Компьютерный томограф Philips iCT 256',
    'Компьютерный томограф Canon Aquilion Lightning',
    'Компьютерный томограф Canon Aquilion Prime SP',
    'Компьютерный томограф Canon Aquilion ONE',
    'Компьютерный томограф Toshiba Aquilion 64',
    'Компьютерный томограф United Imaging uCT 550',
    'Компьютерный томограф United Imaging uCT 780',
    'Компьютерный томограф Neusoft NeuViz 64',
    'Компьютерный томограф Neusoft NeuViz 128',
    'Компьютерный томограф НИПК «Электрон» КТР-Электрон',
  ],
  MR: [
    'Магнитно-резонансный томограф Siemens MAGNETOM Essenza 1,5 Т',
    'Магнитно-резонансный томограф Siemens MAGNETOM Aera 1,5 Т',
    'Магнитно-резонансный томограф Siemens MAGNETOM Sola 1,5 Т',
    'Магнитно-резонансный томограф Siemens MAGNETOM Skyra 3,0 Т',
    'Магнитно-резонансный томограф Siemens MAGNETOM Vida 3,0 Т',
    'Магнитно-резонансный томограф Siemens MAGNETOM Prisma 3,0 Т',
    'Магнитно-резонансный томограф GE Signa Explorer 1,5 Т',
    'Магнитно-резонансный томограф GE Signa Voyager 1,5 Т',
    'Магнитно-резонансный томограф GE Optima MR450w 1,5 Т',
    'Магнитно-резонансный томограф GE Discovery MR750w 3,0 Т',
    'Магнитно-резонансный томограф GE Architect 3,0 Т',
    'Магнитно-резонансный томограф GE Premier 3,0 Т',
    'Магнитно-резонансный томограф Philips Ingenia 1,5 Т',
    'Магнитно-резонансный томограф Philips Ingenia Ambition 1,5 Т',
    'Магнитно-резонансный томограф Philips Achieva 1,5 Т',
    'Магнитно-резонансный томограф Philips Ingenia Elition 3,0 Т',
    'Магнитно-резонансный томограф Canon Vantage Orian 1,5 Т',
    'Магнитно-резонансный томограф Canon Vantage Galan 3,0 Т',
    'Магнитно-резонансный томограф United Imaging uMR 588 1,5 Т',
    'Магнитно-резонансный томограф United Imaging uMR 780 3,0 Т',
    'Магнитно-резонансный томограф Hitachi Echelon Oval 1,5 Т',
    'Магнитно-резонансный томограф Hitachi Aperto Lucent 0,4 Т',
    'Магнитно-резонансный томограф НПФ «Аз» ТМР-0,4',
  ],
  PT: [
    'ПЭТ/КТ-сканер Siemens Biograph mCT',
    'ПЭТ/КТ-сканер Siemens Biograph Horizon',
    'ПЭТ/КТ-сканер Siemens Biograph Vision',
    'ПЭТ/КТ-сканер GE Discovery IQ',
    'ПЭТ/КТ-сканер GE Discovery MI',
    'ПЭТ/КТ-сканер Philips Vereos Digital PET/CT',
    'ПЭТ/КТ-сканер Philips Gemini TF',
    'ПЭТ/КТ-сканер United Imaging uMI 550',
    'ПЭТ/КТ-сканер United Imaging uMI 780',
    'ПЭТ/МРТ-сканер Siemens Biograph mMR',
  ],
  CR: [
    'Рентгенодиагностический комплекс Siemens Ysio Max',
    'Рентгенодиагностический комплекс Siemens Multix Impact',
    'Рентгенодиагностический комплекс GE Discovery XR656',
    'Рентгенодиагностический комплекс Philips DigitalDiagnost C90',
    'Рентгенодиагностический комплекс Canon CXDI',
    'Рентгенодиагностический комплекс НИПК «Электрон» КРТ-Электрон',
    'Рентгенодиагностический комплекс АМИКО РДК ВСМ',
    'Рентгенодиагностический комплекс С-Тех РентгенТест',
    'Палатный рентгеновский аппарат Siemens Mobilett Elara Max',
    'Палатный рентгеновский аппарат GE Optima XR240amx',
    'Флюорограф ФЦ-01 «Электрон»',
    'Флюорограф ПроСкан-7000',
  ],
  MG: [
    'Маммограф Siemens MAMMOMAT Inspiration',
    'Маммограф Siemens MAMMOMAT Revelation',
    'Маммограф GE Senographe Crystal',
    'Маммограф GE Senographe Pristina',
    'Маммограф Hologic Selenia Dimensions',
    'Маммограф Hologic 3Dimensions',
    'Маммограф Planmed Clarity 2D',
    'Маммограф МТЛ «Маммо-4МТ»',
    'Маммограф НИПК «Электрон» МАДИС',
  ],
  PX: [
    'Ортопантомограф Planmeca ProMax 3D',
    'Ортопантомограф Sirona Orthophos SL',
    'Ортопантомограф Vatech PaX-i3D',
    'Ортопантомограф Morita Veraviewepocs 3D',
    'Ортопантомограф Carestream CS 8100 3D',
    'Конусно-лучевой томограф Vatech Green X',
    'Конусно-лучевой томограф NewTom VGi evo',
  ],
  XA: [
    'Ангиографический комплекс Siemens Artis zee',
    'Ангиографический комплекс Siemens Artis Q',
    'Ангиографический комплекс Philips Azurion 7',
    'Ангиографический комплекс GE Innova IGS 530',
    'Ангиографический комплекс Canon Alphenix Core+',
  ],
  US: [
    'Ультразвуковой сканер GE LOGIQ E10',
    'Ультразвуковой сканер GE Voluson E10',
    'Ультразвуковой сканер Philips EPIQ Elite',
    'Ультразвуковой сканер Philips Affiniti 70',
    'Ультразвуковой сканер Samsung RS85',
    'Ультразвуковой сканер Mindray Resona 7',
    'Ультразвуковой сканер Canon Aplio i800',
  ],
});

/** Плоский список: сперва аппараты своей модальности, затем остальные. */
export function аппаратыДляМодальности(modality) {
  const м = String(modality || '').toUpperCase();
  const свои = АППАРАТЫ[м] || [];
  const прочие = Object.entries(АППАРАТЫ)
    .filter(([к]) => к !== м).flatMap(([, с]) => с);
  return [...свои, ...прочие];
}

/** Поиск по подстроке без учёта регистра — для строки поиска над списком. */
export function найтиАппараты(запрос, modality) {
  const все = аппаратыДляМодальности(modality);
  const q = String(запрос || '').trim().toLowerCase();
  if (!q) return все;
  return все.filter((а) => а.toLowerCase().includes(q));
}

// ══════════════════════════════════════════════════════════════
//  ДАТЫ
//  Врач набирает «02071972» или «2.7.1972» — программа обязана сама
//  расставить точки и сказать, если дата невозможна.
// ══════════════════════════════════════════════════════════════

/**
 * Дописать точки по мере набора: 02071972 → 02.07.1972, 2.7.19 → 2.7.19.
 * Функция НЕ достраивает год и не спорит с человеком, пока он печатает.
 */
export function маскаДаты(строка) {
  const ц = String(строка ?? '').replace(/\D/g, '').slice(0, 8);
  if (ц.length <= 2) return ц;
  if (ц.length <= 4) return `${ц.slice(0, 2)}.${ц.slice(2)}`;
  return `${ц.slice(0, 2)}.${ц.slice(2, 4)}.${ц.slice(4)}`;
}

/**
 * Проверка законченной даты ДД.ММ.ГГГГ с учётом длины месяца и високосного
 * года. Пустая строка законна: поле необязательное.
 * @returns {{ок:boolean, причина?:string}}
 */
export function проверитьДату(строка) {
  const s = String(строка ?? '').trim();
  if (!s) return { ок: true };
  const м = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(s);
  if (!м) return { ок: false, причина: 'Дата должна быть в виде ДД.ММ.ГГГГ' };
  const д = +м[1], мес = +м[2], г = +м[3];
  if (мес < 1 || мес > 12) return { ок: false, причина: 'Месяца с таким номером не бывает' };
  if (г < 1900 || г > 2100) return { ок: false, причина: 'Год вне разумных границ (1900–2100)' };
  const дней = [31, (г % 4 === 0 && г % 100 !== 0) || г % 400 === 0 ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][мес - 1];
  if (д < 1 || д > дней) return { ок: false, причина: `В этом месяце ${дней} дней` };
  return { ок: true };
}

export default {
  КОНТРАСТЫ_ГАДОЛИНИЙ, КОНТРАСТЫ_ЙОД, контрастыДляМодальности,
  АППАРАТЫ, аппаратыДляМодальности, найтиАппараты, маскаДаты, проверитьДату,
};
